/**
 * Business Central Web Client link generation for EOS (cloud) companies.
 * Red companies (dbi, bin) are on-premise and not supported.
 */

const EOS_CONFIG: Record<string, { instance: string; company: string }> = {
    'dch': { instance: 'PROD_CH', company: 'Dolder AG' },
    'dit': { instance: 'PROD', company: 'Dolder Massara Italy' },
    'dsp': { instance: 'PROD_ES', company: 'Dolder Espa\u00f1a S.L.' },
    'dfr': { instance: 'PROD_FR', company: 'Eneco SAS' },
    'dpt': { instance: 'PROD_PT', company: 'Dolder Portugal Lda' },
    'dtr': { instance: 'PROD_W1', company: 'Dolder Kimya Tic. Ltd. Sti.' },
};

/**
 * BC page numbers by document type.
 *
 * The posted-document pages follow the NAV card/subform pairing — 130/131 posted sales
 * shipment, 132/133 posted sales invoice, 134/135 posted sales credit memo, 136/137 posted
 * purchase RECEIPT, 138/139 posted purchase INVOICE, 140/141 posted purchase credit memo.
 *
 * `postedPurchaseInvoice` used to be 136 here, which is the receipt page: an invoice number
 * filtered against `Purch. Rcpt. Header` matches nothing, so the link opened an empty card
 * ("Geb. Einkaufslieferung" with every field blank). Verified 2026-08-20 against PROD_CH with
 * PP-CH2600638 / PV-CH2601764 / PM-CH2600003.
 */
const BC_PAGES = {
    postedSalesInvoice: 132,
    postedSalesCreditMemo: 134,
    salesOrder: 42,
    purchaseOrder: 50,
    postedPurchaseReceipt: 136,
    postedPurchaseInvoice: 138,
    postedPurchaseCreditMemo: 140,
    /** Vendor ledger entries — where a journal posting lives when no document card exists. */
    vendorLedgerEntries: 29,
    /** G/L entries — the Hauptbuch line behind a VAT-account movement. */
    generalLedgerEntries: 20,
    fixedAsset: 5600,
} as const;

type BcDocumentType = keyof typeof BC_PAGES;

/**
 * Which BC page can show a posting, keyed on where the document actually IS.
 *
 * Written by the matcher (`DocuWareBcLink.BcDocumentKind`) by looking the number up in the
 * posted-document tables, NOT inferred from the vendor-ledger document type. The type is not
 * a reliable guide: measured over dch, only 1'534 of 4'508 postings typed 'Rechnung' exist as
 * a purchase invoice document, and 117 of 120 typed 'Gutschrift' carry a PV-CH number from
 * the invoice series rather than a credit-memo number. Two thirds of dch AP is posted through
 * the journal and has no document card at all.
 */
export type BcDocumentKind =
    | 'invoice' | 'creditmemo' | 'ledger'
    | 'salesinvoice' | 'salescreditmemo';

const PAGE_BY_KIND: Record<BcDocumentKind, BcDocumentType> = {
    invoice: 'postedPurchaseInvoice',
    creditmemo: 'postedPurchaseCreditMemo',
    ledger: 'vendorLedgerEntries',
    salesinvoice: 'postedSalesInvoice',
    salescreditmemo: 'postedSalesCreditMemo',
};

/**
 * The table-qualified field a page filters on. BC rewrites `filter='No.' IS …` into
 * `filter='<source table>'.'No.' IS …` itself, so an unqualified 'No.' is fine for the card
 * pages — but the vendor-ledger list needs `Document No.`, which has no unqualified form
 * that resolves.
 */
const FILTER_FIELD_BY_KIND: Record<BcDocumentKind, string> = {
    invoice: `'No.'`,
    creditmemo: `'No.'`,
    ledger: `'Vendor Ledger Entry'.'Document No.'`,
    salesinvoice: `'No.'`,
    salescreditmemo: `'No.'`,
};

/** True when the link lands on a ledger entry rather than on a document card. */
export function isLedgerOnlyKind(kind: string | null | undefined): boolean {
    return kind === 'ledger';
}

function buildBcUrl(
    companyCode: string,
    documentNo: string,
    page: number,
    filterField = `'No.'`,
): string | null {
    const config = EOS_CONFIG[companyCode];
    if (!config) return null;
    // Only the spaces are escaped, and the apostrophes are left literal — that is the exact
    // shape of the links that are click-verified today, and BC is fussier about this than the
    // spec suggests. encodeURIComponent on the whole field would also escape the quotes.
    const field = filterField.replace(/ /g, '%20');
    return `https://eos.dolder.com/${config.instance}/?company=${encodeURIComponent(config.company)}&dc=0&page=${page}&filter=${field}%20IS%20'${encodeURIComponent(documentNo)}'`;
}

/** Check if a company is an EOS (cloud BC) company */
export function isEosCompany(companyCode: string): boolean {
    return companyCode in EOS_CONFIG;
}

/** Generate BC Web Client URL for a document. Returns null for non-EOS companies. */
export function getBcDocumentUrl(companyCode: string, documentNo: string, type: BcDocumentType): string | null {
    return buildBcUrl(companyCode, documentNo, BC_PAGES[type]);
}

/** Generate BC Web Client URL for a Fixed Asset Card. Returns null for non-EOS companies. */
export function getBcFixedAssetUrl(companyCode: string, assetNo: string): string | null {
    return buildBcUrl(companyCode, assetNo, BC_PAGES.fixedAsset);
}

/** Shorthand for posted sales invoices (page 132) — backwards compatible */
export function getEosInvoiceUrl(companyCode: string, documentNo: string): string | null {
    return buildBcUrl(companyCode, documentNo, BC_PAGES.postedSalesInvoice);
}

/**
 * BC link for a posted creditor document, from where the document actually is.
 *
 * Returns null on three separate grounds, all of them "we cannot build an honest link":
 * no document number, a company with no reachable BC web client (dbi and bin run on
 * redPoint — `EOS_CONFIG` is the whole gate, so no company is named here), or a posting the
 * matcher has not classified yet (`kind` NULL, e.g. a row written before migration 161 and
 * not yet backfilled). No link beats a link into an empty page.
 */
export function getBcPostedPurchaseUrl(
    companyCode: string,
    documentNo: string | null | undefined,
    kind: string | null | undefined,
): string | null {
    if (kind !== 'invoice' && kind !== 'creditmemo' && kind !== 'ledger') return null;
    return getBcPostedDocumentUrl(companyCode, documentNo, kind);
}

/**
 * BC link for any posted document the app can classify - purchase or sales.
 *
 * The sales kinds exist because a VAT return is mostly made of sales: Kz 81 and Kz 41 are
 * output figures, and until these two pages were added those rows offered no link at all.
 * `kind` is always PROBED against the posted tables server-side, never guessed from a document
 * type; anything unclassified returns null, because no link beats a link into an empty page.
 */
export function getBcPostedDocumentUrl(
    companyCode: string,
    documentNo: string | null | undefined,
    kind: string | null | undefined,
): string | null {
    if (!documentNo) return null;
    if (!kind || !(kind in PAGE_BY_KIND)) return null;
    const k = kind as BcDocumentKind;
    return buildBcUrl(companyCode, documentNo, BC_PAGES[PAGE_BY_KIND[k]], FILTER_FIELD_BY_KIND[k]);
}

/**
 * BC link to the G/L entries behind one document - the Hauptbuch line itself.
 *
 * Page 20 filtered on the document number, which is the only key the curated
 * etl.GlPostingTransactions and BC share. Like every list page, the field has to be
 * table-qualified or the filter does not resolve.
 */
export function getBcGlEntriesUrl(
    companyCode: string,
    documentNo: string | null | undefined,
): string | null {
    if (!documentNo) return null;
    return buildBcUrl(companyCode, documentNo, BC_PAGES.generalLedgerEntries,
        `'G/L Entry'.'Document No.'`);
}

/**
 * What the link will actually open, for a tooltip. A ledger link goes somewhere the user did
 * not ask for, so it has to say so before the click rather than after.
 */
export function bcLinkHint(kind: string | null | undefined): string {
    if (kind === 'ledger') {
        return 'Journal posting — opens the vendor ledger entry. This document has no card in BC.';
    }
    if (kind === 'creditmemo') return 'Open the posted credit memo in Business Central';
    if (kind === 'salesinvoice') return 'Open the posted sales invoice in Business Central';
    if (kind === 'salescreditmemo') return 'Open the posted sales credit memo in Business Central';
    return 'Open the posted invoice in Business Central';
}
